// Atmos kits: twelve pads of atmosphere and gentle sound effects per kit (a 3 × 4 pad grid, like a
// drum kit's 4 × 4), synthesised by the engine (rt/textures.rs and friends — no samples). Every
// day plays one kit on sound slots 20–31. Pads are tuned to the day's key: each has a scale degree
// and an octave (tonic and fifth beds, a third for colour), so tapping any pad fits the music.
//
// Kits are made to be pleasant and unobtrusive: soft attacks, rounded tops, little drive. Each pad
// has a role the example songs use (a bed under an intro, a riser into a drop, a hit on it…).
// `level` is calibrated (atmos-kits calibration: every pad at the same loudness on a held tone).
import type { Envelope } from "./generated/Envelope";
import type { SynthModel } from "./generated/SynthModel";
import type { SynthParams } from "./generated/SynthParams";

export const ATMOS_KIT_IDS = [
  "starlight",
  "deep_ocean",
  "rain_cafe",
  "cathedral",
  "neon_night",
  "forest_dawn",
  "desert_wind",
] as const;
export type AtmosKitId = (typeof ATMOS_KIT_IDS)[number];

/** What a pad does in a song. */
export type AtmosRole =
  | "bed" // a long drone or pad under a section
  | "air" // a texture: wind, rain, dust, water
  | "voice" // a choir or a hum
  | "swell" // a shorter pad swell (a bar or two)
  | "sparkle" // little droplets or glints, or a high shimmer
  | "bell" // a single bell or chime tone
  | "riser" // rises into what comes next
  | "fall" // falls away (opens a breakdown)
  | "hit" // a soft impact on a downbeat
  | "drop"; // a sub drop

export interface AtmosPad {
  name: string;
  role: AtmosRole;
  params: Omit<SynthParams, "note">;
  /** Scale degree of the pitch (0 = tonic, 4 = fifth, 2 = third) and the octave's C (36, 48, 60, 72). */
  degree: number;
  octave: number;
  env: Envelope;
  reverb: number;
  delay: number;
  /** Mix volume relative to the kit's (atmos sits back: see ATMOS_VOLUME). */
  volume?: number;
}

export interface AtmosKit {
  id: AtmosKitId;
  name: string;
  /** A short description for the calendar and the editor. */
  description: string;
  pads: AtmosPad[];
}

const p = (model: SynthModel, o: Partial<SynthParams>): Omit<SynthParams, "note"> => ({
  model,
  cutoff: 0.5,
  resonance: 0.3,
  env_amount: 0.5,
  env_decay: 800,
  glide_ms: 0,
  detune: 0.5,
  tone: 0.5,
  drive: 0.1,
  ...o,
});
const env = (attack_ms: number, decay_ms: number, sustain: number, release_ms: number): Envelope => ({
  attack_ms,
  decay_ms,
  sustain,
  release_ms,
});
const pad = (
  name: string,
  role: AtmosRole,
  params: Omit<SynthParams, "note">,
  degree: number,
  octave: number,
  e: Envelope,
  reverb = 0.5,
  delay = 0.15,
  volume = 1,
): AtmosPad => ({ name, role, params, degree, octave, env: e, reverb, delay, volume });

// Envelopes by role: beds and air fade in and out slowly; swells over a bar; hits and bells ring.
const BED = env(1800, 2000, 1, 2600);
const AIR = env(1400, 2000, 1, 2400);
const VOICE = env(900, 1500, 1, 2000);
const SWELL = env(700, 1200, 0.85, 1400);
const GLINT = env(200, 1000, 1, 2200);
/** A high, soft shimmer over a lift (no ringing bell tones: nothing like wind chimes). */
const SHIMMER = env(450, 1500, 0.9, 2000);
const BELL = env(1, 2500, 1, 1800);
const RISE = env(10, 500, 1, 400);
const FALL = env(5, 500, 1, 700);
const HIT = env(1, 3000, 1, 2500);

export const ATMOS_KITS: Record<AtmosKitId, AtmosKit> = {
  starlight: {
    id: "starlight",
    name: "Starlight",
    description: "shimmering space: auroras, starlight, angel voices",
    pads: [
      pad(
        "Aurora",
        "bed",
        p("drone", { cutoff: 0.58, env_amount: 0.5, env_decay: 3000, detune: 0.7, tone: 0.7, level: 0.616 }),
        0,
        48,
        BED,
      ),
      pad(
        "Deep Space",
        "bed",
        p("drone", {
          cutoff: 0.38,
          resonance: 0.35,
          env_amount: 0.75,
          env_decay: 6000,
          detune: 0.9,
          tone: 0.2,
          level: 0.707,
        }),
        0,
        36,
        BED,
      ),
      pad(
        "Fifth Light",
        "bed",
        p("drone", { cutoff: 0.5, env_amount: 0.45, env_decay: 4000, detune: 0.6, tone: 0.55, level: 0.641 }),
        4,
        48,
        BED,
      ),
      pad(
        "Night Air",
        "air",
        p("texture", { cutoff: 0.42, resonance: 0.3, env_amount: 0.8, tone: 0.0, detune: 0.8, drive: 0, level: 2.75 }),
        0,
        48,
        AIR,
      ),
      pad(
        "Angel Aah",
        "voice",
        p("choir", { cutoff: 0.85, env_amount: 0.5, tone: 0.5, detune: 0.85, drive: 0.12, level: 0.433 }),
        0,
        60,
        VOICE,
      ),
      pad(
        "Halo Swell",
        "swell",
        p("drone", { cutoff: 0.6, env_amount: 0.4, env_decay: 900, detune: 0.5, tone: 0.8, level: 0.624 }),
        2,
        60,
        SWELL,
      ),
      pad(
        "Star Shimmer",
        "sparkle",
        p("drone", { cutoff: 0.62, env_amount: 0.5, env_decay: 2000, detune: 0.8, tone: 0.85, level: 0.571 }),
        4,
        72,
        SHIMMER,
      ),
      pad(
        "Fireflies",
        "sparkle",
        p("sparkle", {
          env_amount: 0.75,
          env_decay: 160,
          tone: 0.05,
          cutoff: 0.75,
          detune: 1,
          drive: 0.25,
          level: 1.197,
        }),
        4,
        72,
        GLINT,
        0.55,
        0.4,
      ),
      pad(
        "Glass Star",
        "bell",
        p("mallet", { tone: 0.75, cutoff: 0.45, env_decay: 2600, resonance: 0.1, detune: 0.15, level: 0.955 }),
        4,
        72,
        BELL,
        0.6,
        0.35,
      ),
      pad(
        "Comet Rise",
        "riser",
        p("sweep", {
          tone: 1,
          env_decay: 3500,
          env_amount: 0.55,
          cutoff: 0.5,
          resonance: 0.4,
          drive: 0.5,
          detune: 0.7,
          level: 0.836,
        }),
        0,
        60,
        RISE,
      ),
      pad(
        "Falling Star",
        "fall",
        p("sweep", {
          tone: 0,
          env_decay: 2500,
          env_amount: 0.7,
          cutoff: 0.55,
          resonance: 0.35,
          drive: 0.6,
          detune: 0.6,
          level: 0.876,
        }),
        0,
        72,
        FALL,
      ),
      pad(
        "Nebula Hit",
        "hit",
        p("impact", {
          env_decay: 3000,
          env_amount: 0.6,
          cutoff: 0.45,
          resonance: 0.55,
          tone: 0.35,
          drive: 0.15,
          level: 1.112,
        }),
        0,
        36,
        HIT,
        0.55,
        0.1,
      ),
    ],
  },
  deep_ocean: {
    id: "deep_ocean",
    name: "Deep Ocean",
    description: "the deep: whale song, surf, sonar pings and bubbles",
    pads: [
      pad(
        "Abyss",
        "bed",
        p("drone", {
          cutoff: 0.32,
          resonance: 0.3,
          env_amount: 0.7,
          env_decay: 6000,
          detune: 0.8,
          tone: 0.1,
          drive: 0.15,
          level: 0.772,
        }),
        0,
        36,
        BED,
      ),
      pad(
        "Current",
        "bed",
        p("drone", { cutoff: 0.44, env_amount: 0.6, env_decay: 4500, detune: 0.7, tone: 0.35, level: 0.7 }),
        4,
        48,
        BED,
      ),
      pad(
        "Surf",
        "air",
        p("texture", {
          cutoff: 0.38,
          resonance: 0.2,
          env_amount: 0.95,
          tone: 0.0,
          detune: 0.9,
          drive: 0,
          level: 3.965,
        }),
        0,
        36,
        AIR,
      ),
      pad(
        "Kelp Glass",
        "air",
        p("texture", {
          cutoff: 0.55,
          resonance: 0.75,
          env_amount: 0.6,
          tone: 0.7,
          detune: 0.6,
          drive: 0,
          level: 1.109,
        }),
        2,
        60,
        AIR,
      ),
      pad(
        "Whale Song",
        "voice",
        p("choir", { cutoff: 0.2, env_amount: 0.95, tone: 0.0, detune: 0.6, drive: 0.4, level: 0.386 }),
        4,
        48,
        VOICE,
      ),
      pad(
        "Tide Swell",
        "swell",
        p("drone", { cutoff: 0.48, env_amount: 0.5, env_decay: 1200, detune: 0.6, tone: 0.4, level: 0.779 }),
        0,
        48,
        SWELL,
      ),
      pad(
        "Bubbles",
        "sparkle",
        p("sparkle", {
          env_amount: 0.55,
          env_decay: 90,
          tone: 0.0,
          cutoff: 0.5,
          detune: 0.8,
          drive: 0.1,
          level: 2.095,
        }),
        0,
        60,
        GLINT,
        0.45,
        0.3,
      ),
      pad(
        "Surface Light",
        "sparkle",
        p("texture", { cutoff: 0.6, resonance: 0.6, env_amount: 0.5, tone: 0.6, detune: 0.8, drive: 0, level: 0.812 }),
        4,
        72,
        SHIMMER,
      ),
      pad(
        "Sonar",
        "bell",
        p("mallet", { tone: 0.25, cutoff: 0.3, env_decay: 3500, resonance: 0.4, detune: 0.0, level: 0.741 }),
        0,
        72,
        BELL,
        0.6,
        0.45,
      ),
      pad(
        "Undertow",
        "riser",
        p("sweep", {
          tone: 1,
          env_decay: 3800,
          env_amount: 0.45,
          cutoff: 0.38,
          resonance: 0.3,
          drive: 0.7,
          detune: 0.5,
          level: 1.226,
        }),
        0,
        48,
        RISE,
      ),
      pad(
        "Sinking",
        "fall",
        p("sweep", {
          tone: 0,
          env_decay: 3000,
          env_amount: 0.6,
          cutoff: 0.4,
          resonance: 0.3,
          drive: 0.75,
          detune: 0.5,
          level: 1.106,
        }),
        0,
        60,
        FALL,
      ),
      pad(
        "Depth Charge",
        "drop",
        p("impact", {
          env_decay: 2500,
          env_amount: 1,
          cutoff: 0.25,
          resonance: 0.15,
          tone: 0.05,
          drive: 0.3,
          level: 1.029,
        }),
        0,
        36,
        HIT,
        0.3,
        0,
      ),
    ],
  },
  rain_cafe: {
    id: "rain_cafe",
    name: "Rain Café",
    description: "a rainy window: drizzle, vinyl, warm hum and a steaming cup",
    pads: [
      pad(
        "Rain on Glass",
        "air",
        p("texture", {
          cutoff: 0.65,
          resonance: 0.5,
          env_amount: 0.5,
          tone: 0.6,
          detune: 0.9,
          drive: 0.7,
          level: 1.111,
        }),
        0,
        60,
        AIR,
      ),
      pad(
        "Vinyl Dust",
        "air",
        p("texture", {
          cutoff: 0.28,
          resonance: 0.55,
          env_amount: 0.3,
          tone: 0.0,
          detune: 0.4,
          drive: 0.55,
          level: 2.526,
        }),
        0,
        48,
        AIR,
        0.25,
        0,
      ),
      pad(
        "Warm Hum",
        "bed",
        p("drone", {
          cutoff: 0.45,
          env_amount: 0.4,
          env_decay: 2500,
          detune: 0.45,
          tone: 0.3,
          drive: 0.45,
          level: 0.648,
        }),
        0,
        48,
        BED,
      ),
      pad(
        "Window Light",
        "bed",
        p("drone", { cutoff: 0.52, env_amount: 0.35, env_decay: 3000, detune: 0.5, tone: 0.55, level: 0.653 }),
        4,
        48,
        BED,
      ),
      pad(
        "Mist Voices",
        "voice",
        p("choir", { cutoff: 0.45, env_amount: 0.6, tone: 0.1, detune: 0.9, drive: 0.5, level: 0.636 }),
        2,
        60,
        VOICE,
      ),
      pad(
        "Cello Swell",
        "swell",
        p("bowed", {
          cutoff: 0.4,
          tone: 0.6,
          detune: 0.0,
          env_amount: 0.35,
          env_decay: 400,
          drive: 0.15,
          level: 1.437,
        }),
        0,
        48,
        SWELL,
      ),
      pad(
        "Steam Glow",
        "sparkle",
        p("drone", { cutoff: 0.5, env_amount: 0.35, env_decay: 1600, detune: 0.4, tone: 0.6, level: 0.668 }),
        4,
        72,
        SHIMMER,
      ),
      pad(
        "Rain Glow",
        "sparkle",
        p("texture", {
          cutoff: 0.58,
          resonance: 0.5,
          env_amount: 0.4,
          tone: 0.7,
          detune: 0.85,
          drive: 0,
          level: 1.134,
        }),
        2,
        72,
        SHIMMER,
      ),
      pad(
        "Teacup Bell",
        "bell",
        p("mallet", { tone: 0.25, cutoff: 0.4, env_decay: 2400, resonance: 0.3, detune: 0.35, level: 0.888 }),
        2,
        72,
        BELL,
      ),
      pad(
        "Steam Rise",
        "riser",
        p("sweep", {
          tone: 1,
          env_decay: 3200,
          env_amount: 0.4,
          cutoff: 0.45,
          resonance: 0.25,
          drive: 0.85,
          detune: 0.4,
          level: 0.764,
        }),
        0,
        60,
        RISE,
      ),
      pad(
        "Settle",
        "fall",
        p("sweep", {
          tone: 0,
          env_decay: 2200,
          env_amount: 0.5,
          cutoff: 0.45,
          resonance: 0.25,
          drive: 0.8,
          detune: 0.4,
          level: 1.022,
        }),
        0,
        60,
        FALL,
      ),
      pad(
        "Far Thunder",
        "hit",
        p("impact", {
          env_decay: 3500,
          env_amount: 0.4,
          cutoff: 0.2,
          resonance: 0.1,
          tone: 0.15,
          drive: 0.1,
          level: 1.028,
        }),
        0,
        36,
        HIT,
        0.5,
        0,
      ),
    ],
  },
  cathedral: {
    id: "cathedral",
    name: "Cathedral",
    description: "stone and light: choirs, deep bells and slow swells",
    pads: [
      pad(
        "Nave",
        "bed",
        p("drone", {
          cutoff: 0.45,
          resonance: 0.15,
          env_amount: 0.3,
          env_decay: 5000,
          detune: 0.55,
          tone: 0.45,
          drive: 0.2,
          level: 0.736,
        }),
        0,
        36,
        BED,
      ),
      pad(
        "Fifth Vault",
        "bed",
        p("drone", { cutoff: 0.5, env_amount: 0.3, env_decay: 4000, detune: 0.5, tone: 0.5, level: 0.655 }),
        4,
        48,
        BED,
      ),
      pad(
        "Choir Aah",
        "voice",
        p("choir", { cutoff: 0.8, env_amount: 0.45, tone: 0.5, detune: 0.85, drive: 0.1, level: 0.434 }),
        0,
        60,
        VOICE,
      ),
      pad(
        "Choir Ooh",
        "voice",
        p("choir", { cutoff: 0.3, env_amount: 0.35, tone: 0.1, detune: 0.75, drive: 0.15, level: 0.589 }),
        4,
        48,
        VOICE,
      ),
      pad(
        "Candle Air",
        "air",
        p("texture", { cutoff: 0.5, resonance: 0.7, env_amount: 0.45, tone: 0.8, detune: 0.6, drive: 0, level: 0.92 }),
        0,
        60,
        AIR,
      ),
      pad(
        "Organ Breath",
        "swell",
        p("drone", { cutoff: 0.55, env_amount: 0.2, env_decay: 900, detune: 0.2, tone: 0.65, level: 0.598 }),
        2,
        60,
        SWELL,
      ),
      pad(
        "Light Voices",
        "sparkle",
        p("choir", { cutoff: 0.6, env_amount: 0.5, tone: 0.6, detune: 0.8, drive: 0.2, level: 0.371 }),
        4,
        72,
        SHIMMER,
      ),
      pad(
        "Tower Bell",
        "bell",
        p("mallet", { tone: 0.75, cutoff: 0.35, env_decay: 4000, resonance: 0.2, detune: 0.0, level: 1.092 }),
        0,
        60,
        BELL,
        0.6,
        0.2,
      ),
      pad(
        "Small Bell",
        "bell",
        p("mallet", { tone: 0.75, cutoff: 0.5, env_decay: 2500, resonance: 0.1, detune: 0.0, level: 0.913 }),
        4,
        72,
        BELL,
        0.6,
        0.25,
      ),
      pad(
        "Ascension",
        "riser",
        p("sweep", {
          tone: 1,
          env_decay: 4000,
          env_amount: 0.5,
          cutoff: 0.45,
          resonance: 0.35,
          drive: 0.4,
          detune: 0.6,
          level: 0.826,
        }),
        0,
        60,
        RISE,
      ),
      pad(
        "Descent",
        "fall",
        p("sweep", {
          tone: 0,
          env_decay: 3000,
          env_amount: 0.55,
          cutoff: 0.45,
          resonance: 0.3,
          drive: 0.5,
          detune: 0.6,
          level: 0.828,
        }),
        0,
        72,
        FALL,
      ),
      pad(
        "Stone Hit",
        "hit",
        p("impact", {
          env_decay: 3500,
          env_amount: 0.5,
          cutoff: 0.35,
          resonance: 0.7,
          tone: 0.3,
          drive: 0.1,
          level: 1.086,
        }),
        0,
        36,
        HIT,
        0.6,
        0,
      ),
    ],
  },
  neon_night: {
    id: "neon_night",
    name: "Neon Night",
    description: "city lights: synth swells, risers, drops and glitter",
    pads: [
      pad(
        "Neon Pad",
        "bed",
        p("super", {
          cutoff: 0.38,
          resonance: 0.1,
          env_amount: 0.1,
          env_decay: 1500,
          detune: 0.55,
          tone: 0.6,
          drive: 0.1,
          level: 0.902,
        }),
        0,
        48,
        BED,
      ),
      pad(
        "Haze",
        "bed",
        p("drone", {
          cutoff: 0.48,
          env_amount: 0.55,
          env_decay: 3000,
          detune: 0.75,
          tone: 0.4,
          drive: 0.25,
          level: 0.654,
        }),
        4,
        48,
        BED,
      ),
      pad(
        "City Air",
        "air",
        p("texture", {
          cutoff: 0.5,
          resonance: 0.4,
          env_amount: 0.7,
          tone: 0.35,
          detune: 0.9,
          drive: 0.2,
          level: 2.388,
        }),
        0,
        48,
        AIR,
      ),
      pad(
        "Siren Vox",
        "voice",
        p("choir", { cutoff: 0.9, env_amount: 0.6, tone: 0.85, detune: 0.5, drive: 0.1, level: 0.711 }),
        4,
        60,
        VOICE,
      ),
      pad(
        "Synth Swell",
        "swell",
        p("super", {
          cutoff: 0.3,
          resonance: 0.25,
          env_amount: 0.3,
          env_decay: 1200,
          detune: 0.6,
          tone: 0.7,
          drive: 0.1,
          level: 1.417,
        }),
        2,
        60,
        SWELL,
      ),
      pad(
        "Neon Glow",
        "sparkle",
        p("super", {
          cutoff: 0.34,
          resonance: 0.15,
          env_amount: 0.25,
          env_decay: 1600,
          detune: 0.75,
          tone: 0.6,
          drive: 0.05,
          level: 5.189,
        }),
        0,
        72,
        SHIMMER,
      ),
      pad(
        "Night Haze",
        "sparkle",
        p("texture", {
          cutoff: 0.55,
          resonance: 0.45,
          env_amount: 0.45,
          tone: 0.65,
          detune: 0.9,
          drive: 0,
          level: 1.147,
        }),
        4,
        72,
        SHIMMER,
      ),
      pad(
        "Neon Bell",
        "bell",
        p("mallet", { tone: 0.5, cutoff: 0.55, env_decay: 1800, resonance: 0.2, detune: 0.0, level: 1.349 }),
        0,
        72,
        BELL,
        0.5,
        0.45,
      ),
      pad(
        "Uplifter",
        "riser",
        p("sweep", {
          tone: 1,
          env_decay: 3500,
          env_amount: 0.65,
          cutoff: 0.55,
          resonance: 0.5,
          drive: 0.55,
          detune: 0.8,
          level: 0.894,
        }),
        0,
        60,
        RISE,
      ),
      pad(
        "Downlifter",
        "fall",
        p("sweep", {
          tone: 0,
          env_decay: 2500,
          env_amount: 0.75,
          cutoff: 0.6,
          resonance: 0.4,
          drive: 0.65,
          detune: 0.7,
          level: 0.904,
        }),
        0,
        72,
        FALL,
      ),
      pad(
        "Sub Drop",
        "drop",
        p("impact", {
          env_decay: 2500,
          env_amount: 1,
          cutoff: 0.3,
          resonance: 0.1,
          tone: 0.1,
          drive: 0.4,
          level: 1.055,
        }),
        0,
        36,
        HIT,
        0.25,
        0,
      ),
      pad(
        "Neon Hit",
        "hit",
        p("impact", {
          env_decay: 2500,
          env_amount: 0.65,
          cutoff: 0.55,
          resonance: 0.5,
          tone: 0.45,
          drive: 0.25,
          level: 1.147,
        }),
        0,
        36,
        HIT,
        0.5,
        0.15,
      ),
    ],
  },
  forest_dawn: {
    id: "forest_dawn",
    name: "Forest Dawn",
    description: "morning woods: birdsong, leaves, a brook and a wooden bell",
    pads: [
      pad(
        "Morning Air",
        "air",
        p("texture", {
          cutoff: 0.48,
          resonance: 0.3,
          env_amount: 0.75,
          tone: 0.0,
          detune: 0.85,
          drive: 0,
          level: 2.662,
        }),
        0,
        48,
        AIR,
      ),
      pad(
        "Brook",
        "air",
        p("texture", {
          cutoff: 0.6,
          resonance: 0.45,
          env_amount: 0.9,
          tone: 0.5,
          detune: 0.95,
          drive: 0.3,
          level: 1.334,
        }),
        4,
        60,
        AIR,
      ),
      pad(
        "Leaves",
        "air",
        p("texture", {
          cutoff: 0.55,
          resonance: 0.2,
          env_amount: 0.85,
          tone: 0.2,
          detune: 1,
          drive: 0.4,
          level: 1.346,
        }),
        0,
        60,
        AIR,
        0.35,
        0,
      ),
      pad(
        "Warm Earth",
        "bed",
        p("drone", {
          cutoff: 0.42,
          env_amount: 0.4,
          env_decay: 3500,
          detune: 0.5,
          tone: 0.3,
          drive: 0.25,
          level: 0.629,
        }),
        0,
        36,
        BED,
      ),
      pad(
        "Old Oak",
        "hit",
        p("impact", {
          env_decay: 2600,
          env_amount: 0.35,
          cutoff: 0.22,
          resonance: 0.45,
          tone: 0.25,
          drive: 0.05,
          level: 1.192,
        }),
        0,
        36,
        HIT,
        0.45,
        0,
      ),
      pad(
        "Wood Flute",
        "swell",
        p("flute", { cutoff: 0.4, tone: 0.2, drive: 0.25, env_amount: 0.3, env_decay: 500, level: 0.859 }),
        4,
        60,
        SWELL,
        0.55,
        0.3,
      ),
      pad(
        "Hum",
        "voice",
        p("choir", { cutoff: 0.35, env_amount: 0.35, tone: 0.05, detune: 0.6, drive: 0.25, level: 0.789 }),
        0,
        48,
        VOICE,
      ),
      pad(
        "Birdsong",
        "sparkle",
        p("sweep", {
          tone: 1,
          env_decay: 110,
          env_amount: 0.25,
          cutoff: 0.8,
          resonance: 0.6,
          drive: 0,
          detune: 0.6,
          level: 0.916,
        }),
        0,
        84,
        env(2, 120, 0.4, 90),
        0.5,
        0.35,
        0.7,
      ),
      pad(
        "Canopy Light",
        "sparkle",
        p("drone", { cutoff: 0.6, env_amount: 0.6, env_decay: 2400, detune: 0.9, tone: 0.75, level: 0.595 }),
        2,
        72,
        SHIMMER,
      ),
      pad(
        "Wood Bell",
        "bell",
        p("mallet", { tone: 0.0, cutoff: 0.45, env_decay: 1400, resonance: 0.4, detune: 0.0, level: 1.072 }),
        4,
        72,
        BELL,
        0.45,
        0.25,
      ),
      pad(
        "Sunrise",
        "riser",
        p("sweep", {
          tone: 1,
          env_decay: 3800,
          env_amount: 0.4,
          cutoff: 0.45,
          resonance: 0.3,
          drive: 0.6,
          detune: 0.5,
          level: 0.921,
        }),
        0,
        60,
        RISE,
      ),
      pad(
        "Settling Mist",
        "fall",
        p("sweep", {
          tone: 0,
          env_decay: 2800,
          env_amount: 0.45,
          cutoff: 0.45,
          resonance: 0.25,
          drive: 0.75,
          detune: 0.5,
          level: 1.029,
        }),
        0,
        60,
        FALL,
      ),
    ],
  },
  desert_wind: {
    id: "desert_wind",
    name: "Desert Wind",
    description: "dunes at dusk: wind, singing sand, a mirage and a far bell",
    pads: [
      pad(
        "Dune Wind",
        "air",
        p("texture", {
          cutoff: 0.44,
          resonance: 0.35,
          env_amount: 0.9,
          tone: 0.1,
          detune: 0.9,
          drive: 0,
          level: 2.719,
        }),
        0,
        48,
        AIR,
      ),
      pad(
        "Singing Sand",
        "air",
        p("texture", {
          cutoff: 0.58,
          resonance: 0.8,
          env_amount: 0.55,
          tone: 0.85,
          detune: 0.7,
          drive: 0,
          level: 0.821,
        }),
        4,
        60,
        AIR,
      ),
      pad(
        "Sand Hiss",
        "air",
        p("texture", { cutoff: 0.7, resonance: 0.15, env_amount: 0.6, tone: 0.4, detune: 1, drive: 0.5, level: 1.11 }),
        0,
        60,
        AIR,
        0.3,
        0,
      ),
      pad(
        "Mirage",
        "bed",
        p("drone", {
          cutoff: 0.5,
          env_amount: 0.65,
          env_decay: 4000,
          detune: 0.8,
          tone: 0.5,
          drive: 0.2,
          level: 0.602,
        }),
        4,
        48,
        BED,
      ),
      pad(
        "Dusk",
        "bed",
        p("drone", {
          cutoff: 0.4,
          env_amount: 0.45,
          env_decay: 4500,
          detune: 0.6,
          tone: 0.25,
          drive: 0.3,
          level: 0.764,
        }),
        0,
        36,
        BED,
      ),
      pad(
        "Oasis",
        "swell",
        p("super", {
          cutoff: 0.32,
          resonance: 0.1,
          env_amount: 0.2,
          env_decay: 1400,
          detune: 0.45,
          tone: 0.5,
          drive: 0.15,
          level: 1.119,
        }),
        0,
        48,
        SWELL,
      ),
      pad(
        "Distant Voice",
        "voice",
        p("choir", { cutoff: 0.55, env_amount: 0.85, tone: 0.3, detune: 0.3, drive: 0.35, level: 0.43 }),
        4,
        60,
        VOICE,
      ),
      pad(
        "Heat Shimmer",
        "sparkle",
        p("texture", {
          cutoff: 0.62,
          resonance: 0.55,
          env_amount: 0.7,
          tone: 0.5,
          detune: 0.9,
          drive: 0.1,
          level: 0.705,
        }),
        0,
        72,
        SHIMMER,
      ),
      pad(
        "Caravan Bell",
        "bell",
        p("mallet", { tone: 1, cutoff: 0.4, env_decay: 2200, resonance: 0.2, detune: 0.1, level: 1.012 }),
        0,
        72,
        BELL,
        0.55,
        0.3,
      ),
      pad(
        "Dust Devil",
        "riser",
        p("sweep", {
          tone: 1,
          env_decay: 3500,
          env_amount: 0.5,
          cutoff: 0.5,
          resonance: 0.3,
          drive: 0.75,
          detune: 0.9,
          level: 0.932,
        }),
        0,
        60,
        RISE,
      ),
      pad(
        "Fading Sun",
        "fall",
        p("sweep", {
          tone: 0,
          env_decay: 3000,
          env_amount: 0.55,
          cutoff: 0.5,
          resonance: 0.3,
          drive: 0.6,
          detune: 0.6,
          level: 0.8,
        }),
        0,
        72,
        FALL,
      ),
      pad(
        "Earth Drum",
        "hit",
        p("impact", {
          env_decay: 2800,
          env_amount: 0.45,
          cutoff: 0.3,
          resonance: 0.35,
          tone: 0.2,
          drive: 0.15,
          level: 1.068,
        }),
        0,
        36,
        HIT,
        0.45,
        0,
      ),
    ],
  },
};

/** Atmos kit preferences per drum kit (weights); every kit stays possible. */
export const ATMOS_KIT_WEIGHTS: Record<string, Partial<Record<AtmosKitId, number>>> = {
  jungle: { forest_dawn: 3, deep_ocean: 2, starlight: 1.5, neon_night: 1 },
  deep_sea: { deep_ocean: 4, starlight: 2, rain_cafe: 1.5, cathedral: 1 },
  trap_lab: { neon_night: 3, rain_cafe: 2, cathedral: 2, desert_wind: 1.5 },
  deep_house: { rain_cafe: 3, starlight: 2, desert_wind: 1.5, deep_ocean: 1 },
  techno: { neon_night: 3, starlight: 2, cathedral: 1.5, deep_ocean: 1.5 },
  disco: { starlight: 3, neon_night: 2, rain_cafe: 1 },
  garage: { rain_cafe: 3, neon_night: 2, starlight: 1.5 },
  afro: { forest_dawn: 3, desert_wind: 3, rain_cafe: 1 },
  synthwave: { neon_night: 3, starlight: 3, desert_wind: 1 },
};

export function getAtmosKit(id: string): AtmosKit {
  return ATMOS_KITS[(ATMOS_KIT_IDS as readonly string[]).includes(id) ? (id as AtmosKitId) : "starlight"];
}
